import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Image,
  TouchableOpacity,
  Linking,
  LayoutAnimation,
  Platform,
  UIManager,
  KeyboardAvoidingView,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Chip,
  Divider,
  ActivityIndicator,
} from 'react-native-paper';
import GradientHeader from '../../components/common/GradientHeader';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {launchImageLibrary} from 'react-native-image-picker';
import Geolocation from '@react-native-community/geolocation';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';
import StatePickerModal from '../../components/StatePickerModal';
import {getStatesForCountryName} from '../../data/states';

if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const ORG_TYPES = [
  {value: 'OUTLET', label: 'Outlet'},
  {value: 'FRANCHISE', label: 'Franchise'},
  {value: 'MASTER_FRANCHISE', label: 'Master Franchise'},
  {value: 'WAREHOUSE', label: 'Warehouse'},
  {value: 'OWNER_GROUP', label: 'Owner Group'},
];

const SectionHeader = ({title}) => (
  <Text style={styles.sectionHeader}>{title}</Text>
);

const ProgressBar = ({step, total, t}) => (
  <View style={styles.progressContainer}>
    <Text style={styles.progressText}>
      {t('store.stepOf', {step, total})}
    </Text>
    <View style={styles.progressBarTrack}>
      <View
        style={[styles.progressBarFill, {width: `${(step / total) * 100}%`}]}
      />
    </View>
  </View>
);

const StoreFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const storeId = route.params?.storeId;
  const isEditing = !!storeId;

  const {
    fetchOrgUnit,
    createOrgUnit,
    updateOrgUnit,
    uploadOrgUnitImages,
  } = useOrgUnitStore();

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [logoImage, setLogoImage] = useState(null);
  const [photoImage, setPhotoImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [stateModalVisible, setStateModalVisible] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().required(t('validation.required')),
    code: isEditing
      ? yup.string()
      : yup.string().required(t('validation.required')),
    orgType: yup.string(),
    phone: yup.string(),
    email: yup.string().email(t('validation.email')),
    website: yup.string(),
    instagram: yup.string(),
    whatsappPhone: yup.string(),
    latitude: yup.string(),
    longitude: yup.string(),
    addressLine1: yup.string(),
    addressLine2: yup.string(),
    area: yup.string(),
    city: yup.string(),
    state: yup.string(),
    country: yup.string(),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      code: '',
      orgType: 'OUTLET',
      phone: '',
      email: '',
      website: '',
      instagram: '',
      whatsappPhone: '',
      latitude: '',
      longitude: '',
      addressLine1: '',
      addressLine2: '',
      area: '',
      city: '',
      state: '',
      country: '',
    },
  });

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetchOrgUnit(storeId)
        .then(store => {
          if (store) {
            if (store.logoUrl) {
              setLogoPreview(store.logoUrl);
            }
            if (store.photoUrl) {
              setPhotoPreview(store.photoUrl);
            }
            reset({
              name: store.name || '',
              code: store.code || '',
              orgType: store.orgType || 'OUTLET',
              phone: store.phone || '',
              email: store.email || '',
              website: store.website || '',
              instagram: store.instagram || '',
              whatsappPhone: store.whatsappPhone || '',
              latitude: store.latitude ? String(store.latitude) : '',
              longitude: store.longitude ? String(store.longitude) : '',
              addressLine1: store.addressLine1 || '',
              addressLine2: store.addressLine2 || '',
              area: store.area || '',
              city: store.city || '',
              state: store.state || '',
              country: store.country || '',
            });
          }
        })
        .catch(e => {
          setSnackbar(e.response?.data?.message || t('common.error'));
        })
        .finally(() => setLoading(false));
    }
  }, [isEditing, storeId, fetchOrgUnit, reset, t]);

  const pickImage = useCallback(async (setter, previewSetter) => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      setter(result.assets[0]);
      previewSetter(result.assets[0].uri);
    }
  }, []);

  const handleNext = useCallback(async () => {
    const valid = await trigger(['name', ...(isEditing ? [] : ['code'])]);
    if (valid) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setStep(2);
    }
  }, [trigger, isEditing]);

  const handleBack = useCallback(() => {
    if (step === 2) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setStep(1);
    } else {
      navigation.goBack();
    }
  }, [step, navigation]);

  const handleOpenMap = useCallback(() => {
    const lat = watch('latitude') || '0';
    const lng = watch('longitude') || '0';
    const url = Platform.select({
      ios: `maps:0,0?q=${lat},${lng}`,
      android: `geo:${lat},${lng}?q=${lat},${lng}`,
    });
    Linking.openURL(url).catch(() => {
      Linking.openURL(
        `https://www.google.com/maps?q=${lat},${lng}`,
      );
    });
  }, [watch]);

  const handleFetchLocation = useCallback(() => {
    setFetchingLocation(true);
    Geolocation.getCurrentPosition(
      position => {
        setValue('latitude', String(position.coords.latitude));
        setValue('longitude', String(position.coords.longitude));
        setFetchingLocation(false);
      },
      error => {
        setSnackbar(error.message || t('common.error'));
        setFetchingLocation(false);
      },
      {enableHighAccuracy: true, timeout: 15000, maximumAge: 10000},
    );
  }, [setValue, t]);

  const onSubmit = useCallback(
    async data => {
      setSaving(true);
      try {
        const payload = {
          ...data,
          latitude: data.latitude ? parseFloat(data.latitude) : null,
          longitude: data.longitude ? parseFloat(data.longitude) : null,
        };

        // Remove code and orgType on edit since they're not editable
        if (isEditing) {
          delete payload.code;
          delete payload.orgType;
        }

        let store;
        if (isEditing) {
          store = await updateOrgUnit(storeId, payload);
        } else {
          store = await createOrgUnit(payload);
        }

        if (logoImage || photoImage) {
          await uploadOrgUnitImages(store.id, logoImage, photoImage);
        }

        navigation.goBack();
      } catch (e) {
        setSnackbar(e.response?.data?.message || t('common.error'));
      } finally {
        setSaving(false);
      }
    },
    [
      isEditing,
      storeId,
      createOrgUnit,
      updateOrgUnit,
      uploadOrgUnitImages,
      navigation,
      t,
      logoImage,
      photoImage,
    ],
  );

  const renderImagePicker = (label, preview, onPick) => (
    <TouchableOpacity style={styles.imagePicker} onPress={onPick}>
      {preview ? (
        <Image source={{uri: preview}} style={styles.imagePreview} />
      ) : (
        <View style={styles.imagePlaceholder}>
          <Text style={styles.imagePlaceholderIcon}>+</Text>
          <Text style={styles.imagePlaceholderText}>{label}</Text>
          <Text style={styles.tapToUpload}>{t('store.tapToUpload')}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.container}>
        <GradientHeader
          title={t('store.editStore')}
          onBack={() => navigation.goBack()}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={isEditing ? t('store.editStore') : t('store.addStore')}
        onBack={handleBack}
      />

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}>
      <ScrollView
        style={styles.form}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <ProgressBar step={step} total={2} t={t} />

        {step === 1 && (
          <View>
            {/* Store Name */}
            <Controller
              control={control}
              name="name"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.name') + ' *'}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={!!errors.name}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            {/* Store Code - only on create */}
            {!isEditing && (
              <Controller
                control={control}
                name="code"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    label={t('store.code') + ' *'}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={!!errors.code}
                    mode="outlined"
                    style={styles.input}
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                  />
                )}
              />
            )}

            {/* Org Type - only on create */}
            {!isEditing && (
              <>
                <Text style={styles.fieldLabel}>{t('store.orgType')}</Text>
                <Controller
                  control={control}
                  name="orgType"
                  render={({field: {onChange, value}}) => (
                    <View style={styles.chipRow}>
                      {ORG_TYPES.map(type => (
                        <Chip
                          key={type.value}
                          selected={value === type.value}
                          onPress={() => onChange(type.value)}
                          style={[
                            styles.chip,
                            value === type.value && styles.chipSelected,
                          ]}
                          textStyle={[
                            styles.chipText,
                            value === type.value && styles.chipTextSelected,
                          ]}
                          showSelectedOverlay={false}
                          icon={
                            value === type.value
                              ? 'check-circle'
                              : 'circle-outline'
                          }>
                          {type.label}
                        </Chip>
                      ))}
                    </View>
                  )}
                />
              </>
            )}

            <Divider style={styles.divider} />

            {/* Contact Fields */}
            <Controller
              control={control}
              name="phone"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.phone')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="phone-pad"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                  left={<TextInput.Icon icon="phone" />}
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.email')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={!!errors.email}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                  left={<TextInput.Icon icon="email" />}
                />
              )}
            />

            <Controller
              control={control}
              name="website"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.website')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="url"
                  autoCapitalize="none"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                  left={<TextInput.Icon icon="web" />}
                />
              )}
            />

            <Controller
              control={control}
              name="instagram"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.instagram')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  autoCapitalize="none"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                  left={<TextInput.Icon icon="instagram" />}
                />
              )}
            />

            <Controller
              control={control}
              name="whatsappPhone"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.whatsapp')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  keyboardType="phone-pad"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                  left={<TextInput.Icon icon="whatsapp" />}
                />
              )}
            />

            {/* Image Pickers */}
            <Divider style={styles.divider} />
            <View style={styles.imageRow}>
              {renderImagePicker(t('store.storeLogo'), logoPreview, () =>
                pickImage(setLogoImage, setLogoPreview),
              )}
              {renderImagePicker(t('store.storePhoto'), photoPreview, () =>
                pickImage(setPhotoImage, setPhotoPreview),
              )}
            </View>

            {Object.keys(errors).length > 0 && (
              <Text style={styles.errorText}>
                {errors[Object.keys(errors)[0]]?.message}
              </Text>
            )}

            <Button
              mode="contained"
              onPress={handleNext}
              style={styles.actionButton}
              buttonColor={colors.primary}
              contentStyle={styles.buttonContent}>
              {t('common.next')}
            </Button>
          </View>
        )}

        {step === 2 && (
          <View>
            <SectionHeader title={t('store.locationAddress')} />

            {/* Open Map + Current Location */}
            <TouchableOpacity
              style={styles.mapTouchable}
              onPress={handleOpenMap}
              activeOpacity={0.7}>
              <View style={styles.mapContent}>
                <Text style={styles.mapIcon}>{'\uD83D\uDDFA\uFE0F'}</Text>
                <View style={{flex: 1}}>
                  <Text style={styles.mapTitle}>{t('store.openMap')}</Text>
                  <Text style={styles.mapSubtitle}>
                    Tap to view on map
                  </Text>
                </View>
                <TextInput.Icon icon="open-in-new" />
              </View>
            </TouchableOpacity>

            <Button
              mode="outlined"
              onPress={handleFetchLocation}
              loading={fetchingLocation}
              disabled={fetchingLocation}
              style={styles.locationButton}
              textColor={colors.primary}
              icon="crosshairs-gps">
              Use Current Location
            </Button>

            {/* Latitude & Longitude */}
            <View style={styles.row}>
              <View style={styles.halfField}>
                <Controller
                  control={control}
                  name="latitude"
                  render={({field: {onChange, onBlur, value}}) => (
                    <TextInput
                      label={t('store.latitude')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      mode="outlined"
                      style={styles.input}
                      keyboardType="numeric"
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                    />
                  )}
                />
              </View>
              <View style={styles.halfField}>
                <Controller
                  control={control}
                  name="longitude"
                  render={({field: {onChange, onBlur, value}}) => (
                    <TextInput
                      label={t('store.longitude')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      mode="outlined"
                      style={styles.input}
                      keyboardType="numeric"
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                    />
                  )}
                />
              </View>
            </View>

            <Divider style={styles.divider} />

            {/* Address Fields */}
            <Controller
              control={control}
              name="addressLine1"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.addressLine1')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            <Controller
              control={control}
              name="addressLine2"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.addressLine2')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            <Controller
              control={control}
              name="area"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.area')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            <Controller
              control={control}
              name="city"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.city')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            <Controller
              control={control}
              name="state"
              render={({field: {onChange, value}}) => {
                const availableStates = getStatesForCountryName(watch('country'));
                return availableStates ? (
                  <TouchableOpacity activeOpacity={0.7} onPress={() => setStateModalVisible(true)}>
                    <View pointerEvents="none">
                      <TextInput
                        label={t('store.state')}
                        value={value}
                        mode="outlined"
                        style={styles.input}
                        outlineColor={colors.border}
                        activeOutlineColor={colors.primary}
                        editable={false}
                        right={<TextInput.Icon icon="chevron-down" />}
                      />
                    </View>
                  </TouchableOpacity>
                ) : (
                  <TextInput
                    label={t('store.state')}
                    value={value}
                    onChangeText={onChange}
                    mode="outlined"
                    style={styles.input}
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                  />
                );
              }}
            />

            <StatePickerModal
              visible={stateModalVisible}
              onClose={() => setStateModalVisible(false)}
              onSelect={selectedState => setValue('state', selectedState)}
              states={getStatesForCountryName(watch('country'))}
            />

            <Controller
              control={control}
              name="country"
              render={({field: {onChange, onBlur, value}}) => (
                <TextInput
                  label={t('store.country')}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  mode="outlined"
                  style={styles.input}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              )}
            />

            <Button
              mode="contained"
              onPress={handleSubmit(onSubmit)}
              loading={saving}
              disabled={saving}
              style={styles.actionButton}
              buttonColor={colors.primary}
              contentStyle={styles.buttonContent}>
              {t('common.save')}
            </Button>
          </View>
        )}
      </ScrollView>
      </KeyboardAvoidingView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}
        action={{label: t('common.ok'), onPress: () => setSnackbar('')}}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {padding: spacing.md},
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressContainer: {
    marginBottom: spacing.lg,
  },
  progressText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    fontWeight: '500',
  },
  progressBarTrack: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  fieldLabel: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  chip: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  chipSelected: {
    backgroundColor: '#E8F5E9',
    borderColor: colors.primary,
  },
  chipText: {
    color: colors.textSecondary,
    fontSize: 13,
  },
  chipTextSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  divider: {
    marginVertical: spacing.md,
  },
  imageRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  imagePicker: {
    flex: 1,
    height: 150,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    overflow: 'hidden',
    backgroundColor: colors.surface,
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  imagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholderIcon: {
    fontSize: 32,
    color: colors.textLight,
  },
  imagePlaceholderText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  tapToUpload: {
    fontSize: 11,
    color: colors.textLight,
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  halfField: {
    flex: 1,
  },
  mapTouchable: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  mapContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  mapIcon: {
    fontSize: 28,
  },
  mapTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  mapSubtitle: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  locationButton: {
    marginBottom: spacing.md,
    borderColor: colors.primary,
    borderRadius: 8,
  },
  errorText: {color: '#D32F2F', marginBottom: spacing.sm, fontSize: 12},
  actionButton: {
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
    borderRadius: 8,
  },
  buttonContent: {
    paddingVertical: spacing.xs,
  },
});

export default StoreFormScreen;
